import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { createAuthToken } from '#src/lib/auth';
import { logger } from '#src/lib/logger';
import {
  selectAuthOrProspectUser,
  selectAuthToken,
} from '#src/store/session/selectors';
import { setAuthToken } from '#src/store/session/actions';

export function useAuthToken() {
  const authToken = useSelector(selectAuthToken);
  const dispatch = useDispatch();
  const user = useSelector(selectAuthOrProspectUser);
  const [error, setError] = useState();
  const [isLoading, setIsLoading] = useState(false);
  // One try per user: createAuthToken answers a failure (e.g. a 401 with no
  // JWT cookie) with no token, and retrying on every render posted
  // auth/token in a loop. A different user (signing in) gets a new try.
  const triedFor = useRef();

  useEffect(() => {
    const userKey = user?.id ?? null;
    if (isLoading || authToken || triedFor.current === userKey) return;

    triedFor.current = userKey;
    setIsLoading(true);
    setError(undefined);

    (async () => {
      try {
        const token = await createAuthToken(user);
        if (token) dispatch(setAuthToken(token));
        else setError(true);
      } catch (e) {
        logger.error(`[useAuthToken] ${e.message}`, { e });
        setError(true);
      }
      setIsLoading(false);
    })();
  }, [authToken, dispatch, user, isLoading]);

  return {
    authToken,
    error,
    isLoading,
  };
}
